/**
 * Sign-in screen. Passkey + email-OTP are the first-class paths; the
 * legacy invite-code + PIN step is reachable via ?legacy=1 for the
 * 30-day grace window. The code step resolves family invite codes only
 * (circle invites are email magic-links now) and routes to the "who are
 * you?" roster picker → PIN.
 *
 * First-time families get a "Start a family" link → /onboarding (where
 * the single-household lock applies).
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { isSupported as passkeySupported, signInWithPasskey } from '../lib/passkey';

interface Roster {
  familyId: string;
  familyName: string;
  members: { id: string; displayName: string; role: 'parent' | 'youth' }[];
}

type CodeLookupResponse = { kind: 'family' } & Roster;

type Step =
  | { kind: 'home' }
  | { kind: 'code' }
  | { kind: 'who'; roster: Roster; code: string }
  | { kind: 'pin'; roster: Roster; code: string; memberId: string }
  | { kind: 'email-request' }
  | { kind: 'email-verify'; email: string }
  | { kind: 'submitting' };

export default function SignInPage() {
  const [step, setStep] = useState<Step>({ kind: 'home' });
  const [err, setErr] = useState<string | null>(null);
  // ?legacy=1 reveals the "Invite code + PIN" button on the home step.
  // Phase 3 hides it by default; this escape hatch keeps the path
  // reachable for any household that hasn't migrated to email/passkey
  // yet without dragging legacy chrome into the default UX.
  const [searchParams] = useSearchParams();
  const showLegacyPin = searchParams.get('legacy') === '1';

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <div className="relative w-full aspect-[16/9] sm:aspect-[2/1] overflow-hidden bg-ink">
        <img
          src="/hero.png"
          alt="A stone cairn by the Jordan at sunset, with a lit flame at its heart and 12 Stones written across it."
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-paper"
        />
      </div>

      <div className="flex-1 flex flex-col items-center px-6 -mt-6">
        <p className="font-serif italic text-soft text-xl text-center">
          Remember what He's done.
        </p>

        {(step.kind === 'home' || step.kind === 'code') && (
          <div className="card max-w-md w-full mt-6 p-5">
            <div className="kicker text-oxblood">Joshua 4:21–24</div>
            <p className="verse-body text-ink text-base mt-2">
              In the future, when your children ask their fathers, "What is the meaning of
              these stones?" you should tell your children, "Israel crossed the Jordan on
              dry ground"… so that you may always fear the Lord your God.
            </p>
          </div>
        )}

        <div className="w-full max-w-md mt-6">
          {step.kind === 'home' && (
            <HomeStep
              onPasskeyDone={() => {
                // Hard-reload so useAuth re-fetches /me with the new
                // cookie. Same pattern as the legacy PIN path.
                window.location.href = '/today';
              }}
              onEmail={() => {
                setErr(null);
                setStep({ kind: 'email-request' });
              }}
              onUseCode={() => {
                setErr(null);
                setStep({ kind: 'code' });
              }}
              showLegacyPin={showLegacyPin}
              onError={setErr}
            />
          )}
          {step.kind === 'email-request' && (
            <EmailRequestStep
              onSent={(email) => {
                setErr(null);
                setStep({ kind: 'email-verify', email });
              }}
              onBack={() => setStep({ kind: 'home' })}
              onError={setErr}
            />
          )}
          {step.kind === 'email-verify' && (
            <EmailVerifyStep
              email={step.email}
              onBack={() => setStep({ kind: 'email-request' })}
              onError={setErr}
              onSubmitting={() => setStep({ kind: 'submitting' })}
            />
          )}
          {step.kind === 'code' && (
            <CodeStep
              onFamily={(roster, code) => {
                setErr(null);
                setStep({ kind: 'who', roster, code });
              }}
              onBack={() => setStep({ kind: 'home' })}
              onError={setErr}
            />
          )}
          {step.kind === 'who' && (
            <WhoStep
              roster={step.roster}
              code={step.code}
              onPick={(memberId) =>
                setStep({ kind: 'pin', roster: step.roster, code: step.code, memberId })
              }
              onBack={() => setStep({ kind: 'code' })}
            />
          )}
          {step.kind === 'pin' && (
            <PinStep
              roster={step.roster}
              code={step.code}
              memberId={step.memberId}
              onBack={() =>
                setStep({ kind: 'who', roster: step.roster, code: step.code })
              }
              onError={setErr}
              onSubmitting={() => setStep({ kind: 'submitting' })}
            />
          )}
          {step.kind === 'submitting' && (
            <p className="text-center text-soft">Signing in…</p>
          )}

          {err && <p className="text-oxblood text-sm mt-4 text-center">{err}</p>}
        </div>

        <p className="text-muted text-sm mt-8 italic">Private to your family. Always.</p>
        <Link to="/onboarding" className="mt-2 text-sm text-oxblood underline">
          New here? Start or join a family.
        </Link>
        <div className="h-8" />
      </div>
    </div>
  );
}

function CodeStep({
  onFamily,
  onBack,
  onError,
}: {
  onFamily: (roster: Roster, code: string) => void;
  onBack?: () => void;
  onError: (e: string | null) => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (code.trim().length < 4) return;
    setBusy(true);
    onError(null);
    const upper = code.trim().toUpperCase();
    try {
      const res = await api.get<CodeLookupResponse>(
        `/auth/code-lookup?code=${encodeURIComponent(upper)}`,
      );
      onFamily(res, upper);
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-4"
    >
      <label className="block">
        <span className="block kicker text-soft mb-2">Family invite code</span>
        <input
          autoFocus
          autoCapitalize="characters"
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className="w-full h-14 px-4 rounded-xl border border-line bg-card text-2xl tracking-[0.2em] font-mono text-center focus:outline-none focus:border-oxblood"
          placeholder="ABCD2345"
          maxLength={16}
        />
      </label>
      <button
        type="submit"
        disabled={busy || code.trim().length < 4}
        className="w-full h-14 rounded-2xl bg-oxblood text-paper text-lg font-medium disabled:opacity-50"
      >
        {busy ? 'Looking up…' : 'Continue'}
      </button>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="block w-full text-center text-soft text-sm mt-2"
        >
          ← back
        </button>
      )}
    </form>
  );
}

function WhoStep({
  roster,
  code,
  onPick,
  onBack,
}: {
  roster: Roster;
  code: string;
  onPick: (memberId: string) => void;
  onBack: () => void;
}) {
  const nav = useNavigate();
  return (
    <div>
      <button onClick={onBack} className="text-soft text-sm mb-3">
        ← different code
      </button>
      <div className="kicker text-oxblood">{roster.familyName}</div>
      <h2 className="font-serif text-2xl text-ink mt-1 mb-4">Who are you?</h2>
      <div className="space-y-2">
        {roster.members.map((m) => (
          <button
            key={m.id}
            onClick={() => onPick(m.id)}
            className="card w-full p-4 text-left flex items-center justify-between hover:bg-line/30"
          >
            <span className="font-serif text-xl text-ink">{m.displayName}</span>
            <span className="text-xs text-muted uppercase tracking-wider">
              {m.role === 'parent' ? 'Parent' : 'Youth'}
            </span>
          </button>
        ))}
        <button
          onClick={() => nav(`/onboarding?join=${encodeURIComponent(code)}`)}
          className="card w-full p-4 text-left flex items-center justify-between border-2 border-dashed border-oxblood/40 hover:bg-line/30"
        >
          <span className="font-serif text-xl text-oxblood">
            + I'm new — add me to {roster.familyName}
          </span>
        </button>
      </div>
    </div>
  );
}

function PinStep({
  roster,
  code,
  memberId,
  onBack,
  onError,
  onSubmitting,
}: {
  roster: Roster;
  code: string;
  memberId: string;
  onBack: () => void;
  onError: (e: string | null) => void;
  onSubmitting: () => void;
}) {
  const [pin, setPin] = useState('');
  const me = roster.members.find((m) => m.id === memberId);

  async function submit() {
    if (pin.length < 4) return;
    onError(null);
    onSubmitting();
    try {
      await api.post('/auth/sign-in', { inviteCode: code, memberId, pin });
      window.location.href = '/today';
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
      window.history.replaceState(null, '', '/');
      onBack();
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-4"
    >
      <button type="button" onClick={onBack} className="text-soft text-sm">
        ← not me
      </button>
      <div>
        <div className="kicker text-oxblood">{roster.familyName}</div>
        <h2 className="font-serif text-2xl text-ink mt-1">
          Hi, {me?.displayName ?? '…'}.
        </h2>
        <p className="text-soft text-sm mt-1">Enter your PIN to sign in.</p>
      </div>
      <input
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
        type="password"
        pattern="\d{4,8}"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
        className="w-full h-16 px-4 rounded-xl border border-line bg-card text-3xl tracking-[0.4em] font-mono text-center focus:outline-none focus:border-oxblood"
        placeholder="••••"
        maxLength={8}
      />
      <button
        type="submit"
        disabled={pin.length < 4}
        className="w-full h-14 rounded-2xl bg-oxblood text-paper text-lg font-medium disabled:opacity-50"
      >
        Sign in
      </button>
    </form>
  );
}

// ----------------------------------------------------------- home step
// Backlog #9 sunsets the 8-char invite-code + PIN flow as a primary path.
// Passkey + email OTP are now the only first-class entries. The legacy
// invite-code path is still reachable via `?legacy=1` for the 30-day
// grace window so families upgrading from the old onboarding don't get
// locked out; full removal is scheduled in a follow-up PR.
function HomeStep({
  onPasskeyDone,
  onEmail,
  onUseCode,
  showLegacyPin,
  onError,
}: {
  onPasskeyDone: () => void;
  onEmail: () => void;
  onUseCode: () => void;
  showLegacyPin: boolean;
  onError: (e: string | null) => void;
}) {
  const [pending, setPending] = useState(false);
  const supported = passkeySupported();

  async function withPasskey() {
    onError(null);
    setPending(true);
    try {
      await signInWithPasskey();
      onPasskeyDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // User-dismissed prompts surface as NotAllowedError / cancel —
      // don't spam the screen with that; just leave the buttons
      // available again.
      if (/cancel|abort|notallowed/i.test(msg)) {
        onError(null);
      } else {
        onError(msg);
      }
      setPending(false);
    }
  }

  return (
    <div className="space-y-3">
      {supported && (
        <button
          onClick={() => void withPasskey()}
          disabled={pending}
          className="w-full h-14 rounded-2xl bg-oxblood text-paper text-lg font-medium disabled:opacity-50"
        >
          {pending ? 'Waiting on device…' : 'Use Face ID / Touch ID'}
        </button>
      )}
      <button
        onClick={onEmail}
        disabled={pending}
        className="w-full h-14 rounded-2xl border-2 border-oxblood text-oxblood text-lg font-medium disabled:opacity-50"
      >
        Email me a code
      </button>
      {showLegacyPin && (
        <button
          onClick={onUseCode}
          disabled={pending}
          className="w-full text-soft text-xs pt-1"
        >
          Use invite code + PIN (legacy)
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------- email steps

function EmailRequestStep({
  onSent,
  onBack,
  onError,
}: {
  onSent: (email: string) => void;
  onBack: () => void;
  onError: (e: string | null) => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!email.trim()) return;
    setBusy(true);
    onError(null);
    try {
      await api.post('/auth/email/request-otp', { email: email.trim() });
      onSent(email.trim().toLowerCase());
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-4"
    >
      <label className="block">
        <span className="block kicker text-soft mb-2">Email</span>
        <input
          autoFocus
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full h-14 px-4 rounded-xl border border-line bg-card text-lg focus:outline-none focus:border-oxblood"
          placeholder="you@example.com"
        />
      </label>
      <button
        type="submit"
        disabled={busy || !email.trim()}
        className="w-full h-14 rounded-2xl bg-oxblood text-paper text-lg font-medium disabled:opacity-50"
      >
        {busy ? 'Sending…' : 'Send code'}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="block w-full text-center text-soft text-sm"
      >
        ← back
      </button>
    </form>
  );
}

function EmailVerifyStep({
  email,
  onBack,
  onError,
  onSubmitting,
}: {
  email: string;
  onBack: () => void;
  onError: (e: string | null) => void;
  onSubmitting: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!/^\d{6}$/.test(code.trim())) return;
    setBusy(true);
    onError(null);
    onSubmitting();
    try {
      await api.post('/auth/email/verify-otp', { email, code: code.trim() });
      window.location.href = '/today';
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-4"
    >
      <p className="text-soft text-sm">
        We sent a 6-digit code to <span className="text-ink">{email}</span>. Enter it
        below within 10 minutes.
      </p>
      <input
        autoFocus
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        className="w-full h-14 px-4 rounded-xl border border-line bg-card text-2xl tracking-[0.3em] font-mono text-center focus:outline-none focus:border-oxblood"
        placeholder="123456"
        maxLength={6}
      />
      <button
        type="submit"
        disabled={busy || !/^\d{6}$/.test(code.trim())}
        className="w-full h-14 rounded-2xl bg-oxblood text-paper text-lg font-medium disabled:opacity-50"
      >
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="block w-full text-center text-soft text-sm"
      >
        ← use a different email
      </button>
    </form>
  );
}
